/** Manage-palettes modal — Tier 3 Group D Slice 1b.
 *
 *  Surfaces the user-palette CRUD that the inline picker
 *  swatches strip can't fit. Built-ins are read-only and don't
 *  appear here at all (they live in `builtin-palettes.ts` and
 *  surface in the picker's palette dropdown).
 *
 *  Single-purpose dialog: list, create, rename, delete, import,
 *  export user palettes. Import / export uses the JSON format
 *  defined in `lib/palettes-json.ts`. */

import { useEffect, useRef, useState } from 'react';
import { Download, Palette as PaletteIcon, Plus, Trash2, Upload, X } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { usePalettes } from '../lib/use-palettes';
import { PALETTE_LIMITS } from '../lib/user-palettes-prefs';
import { exportPalettesToJson, importPalettesFromJson } from '../lib/palettes-json';

interface Props {
  onClose: () => void;
}

export function PalettesDialog({ onClose }: Props) {
  const { userPalettes, newPalette, rename, remove, removeColor } = usePalettes();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close on Escape — matches the rest of the editor's modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onExportAll = () => {
    if (userPalettes.length === 0) {
      toast({
        title: 'Nothing to export',
        description: 'Create or duplicate a palette first.',
      });
      return;
    }
    const json = exportPalettesToJson(userPalettes);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fias-palettes.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const triggerImport = () => fileInputRef.current?.click();

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    file
      .text()
      .then((text) => {
        const result = importPalettesFromJson(text);
        if (result.error) {
          toast({
            title: 'Import failed',
            description: result.error,
            variant: 'destructive',
          });
          return;
        }
        let created = 0;
        let skipped = 0;
        for (const p of result.palettes) {
          if (userPalettes.length + created >= PALETTE_LIMITS.maxPalettes) {
            skipped++;
            continue;
          }
          newPalette(p.name, p.colors);
          created++;
        }
        const parts = [`Imported ${created} palette${created === 1 ? '' : 's'}.`];
        if (skipped > 0) {
          parts.push(
            `Skipped ${skipped} — would exceed the ${PALETTE_LIMITS.maxPalettes}-palette cap.`,
          );
        }
        toast({ title: 'Import complete', description: parts.join(' ') });
      })
      .catch(() => {
        toast({
          title: 'Import failed',
          description: 'Could not read the file.',
          variant: 'destructive',
        });
      });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label="Manage palettes"
        aria-modal="true"
        className="bg-background border border-border rounded-lg shadow-xl w-[92vw] max-w-xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
          <PaletteIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold flex-1">Palettes</h2>
          <button
            type="button"
            onClick={onExportAll}
            title="Export all palettes as JSON"
            aria-label="Export all palettes as JSON"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={triggerImport}
            title="Import palettes from JSON"
            aria-label="Import palettes from JSON"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <Upload className="h-4 w-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportFile}
            className="hidden"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {userPalettes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No user palettes yet. Create one to start collecting colors.
            </p>
          ) : (
            userPalettes.map((p) => (
              <PaletteRow
                key={p.id}
                palette={p}
                onRename={(name) => rename(p.id, name)}
                onDelete={() => remove(p.id)}
                onRemoveColor={(c) => removeColor(p.id, c)}
              />
            ))
          )}
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-t border-border shrink-0">
          <button
            type="button"
            onClick={() => newPalette('Untitled palette')}
            disabled={userPalettes.length >= PALETTE_LIMITS.maxPalettes}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:hover:bg-primary"
          >
            <Plus className="h-4 w-4" />
            New palette
          </button>
          <span className="text-[11px] text-muted-foreground">
            {userPalettes.length} / {PALETTE_LIMITS.maxPalettes}
          </span>
        </div>
      </div>
    </div>
  );
}

function PaletteRow({
  palette,
  onRename,
  onDelete,
  onRemoveColor,
}: {
  palette: { id: string; name: string; colors: string[] };
  onRename: (name: string) => void;
  onDelete: () => void;
  onRemoveColor: (color: string) => void;
}) {
  const [nameDraft, setNameDraft] = useState(palette.name);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div className="border border-border rounded-md p-3 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={() => {
            if (nameDraft.trim() && nameDraft !== palette.name) {
              onRename(nameDraft);
            } else {
              setNameDraft(palette.name);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setNameDraft(palette.name);
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="flex-1 min-w-0 text-sm font-medium bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none px-1 py-0.5"
          aria-label="Palette name"
        />
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {palette.colors.length}
        </span>
        {confirmingDelete ? (
          <>
            <button
              type="button"
              onClick={onDelete}
              className="text-xs px-2 py-1 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="text-xs px-2 py-1 rounded hover:bg-accent text-muted-foreground"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            aria-label={`Delete palette ${palette.name}`}
            title="Delete palette"
            onClick={() => setConfirmingDelete(true)}
            className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {palette.colors.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic">
          Empty. Add colors from the picker's swatches panel.
        </p>
      ) : (
        <div className="grid grid-cols-10 gap-1">
          {palette.colors.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Remove ${c} from ${palette.name}`}
              title={`${c} — click to remove`}
              onClick={() => onRemoveColor(c)}
              className="group relative aspect-square rounded-sm border border-border hover:scale-110 transition-transform"
              style={{ background: c }}
            >
              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 rounded-sm">
                <X className="h-3 w-3 text-white" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
